// @flow
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import {Visibility, Warning} from '@material-ui/icons';
import {makeStyles} from '@material-ui/core/styles';
import {colors} from '../../../../../assets/styles/colors';
import CustomChip from '../../../../../elements/CustomChip';
import {getStringDate} from '../../../../../utils/date';
import type {
  CustomerComplaint,
  CustomerComplaintState,
} from '../../../../../entities/customer-complaints';
import type {Pagination} from '../../../../../entities/pagination';
import {useIsFeatureInTier} from '../../../../../utils/component';

type Props = {|
  complaints: CustomerComplaint[],
  pagination: Pagination,
  loading: boolean,
  onUpdateStatus: (
    complaintId: string,
    newStatus: CustomerComplaintState
  ) => void,
  onViewDetail: (complaint: CustomerComplaint) => void,
  onChangePage: (newPage: number) => void,
  onChangePageSize: (newSize: number) => void,
  isComplaintOverdue: (complaint: CustomerComplaint) => boolean,
|};

const useStyles = makeStyles(theme => ({
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('sm')]: {
      display: 'none', // Hide table on mobile
    },
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: colors.grey100,
  },
  tableHeadCell: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.grey900,
  },
  tableCell: {
    fontSize: '0.875rem',
    color: colors.grey800,
  },
  correlativeCell: {
    fontWeight: 600,
    color: colors.grey900,
  },
  statusChip: {
    minWidth: 80,
  },
  overdueRow: {
    backgroundColor: '#fff5f5',
  },
  overdueIcon: {
    color: colors.error,
    fontSize: '1.2rem',
  },
  overdueCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionButton: {
    padding: '4px 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '16px',
    cursor: 'pointer',
    border: 'none',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&:hover': {
      opacity: 0.8,
    },
  },
  solveButton: {
    backgroundColor: colors.grey200,
    color: colors.success,
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  receivedButton: {
    backgroundColor: colors.grey200,
    color: colors.danger,
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
}));

const getStatusColor = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'warning';
    case 'SOLVED':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'Recibido';
    case 'SOLVED':
      return 'Resuelto';
    default:
      return status;
  }
};

export default function ComplaintsTable(props: Props) {
  const {complaints, onUpdateStatus, isComplaintOverdue} = props;

  const hasCustomerComplaintFeature = useIsFeatureInTier('CUSTOMER_COMPLAINT');

  const classes = useStyles();

  const handleStatusChange = (complaint: CustomerComplaint) => {
    const newStatus = complaint.state === 'RECEIVED' ? 'SOLVED' : 'RECEIVED';
    onUpdateStatus(complaint.id, newStatus);
  };

  return (
    <Paper className={classes.tableContainer}>
      <TableContainer>
        <Table className={classes.table} aria-label="complaints table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Id</TableCell>
              <TableCell className={classes.tableHeadCell}>
                Correlativo
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                Fecha Creación
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Email</TableCell>
              <TableCell className={classes.tableHeadCell}>Teléfono</TableCell>
              <TableCell className={classes.tableHeadCell}>Estado</TableCell>
              <TableCell className={classes.tableHeadCell}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {complaints.map(complaint => {
              const isOverdue = isComplaintOverdue(complaint);
              return (
                <TableRow
                  key={complaint.id}
                  className={isOverdue ? classes.overdueRow : ''}
                >
                  <TableCell
                    className={`${classes.tableCell} ${classes.correlativeCell}`}
                  >
                    <div className={classes.overdueCell}>
                      {isOverdue && (
                        <Tooltip title="Reclamo vencido - Más de 10 días">
                          <Warning className={classes.overdueIcon} />
                        </Tooltip>
                      )}
                      {complaint.id}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`${classes.tableCell} ${classes.correlativeCell}`}
                  >
                    <div className={classes.overdueCell}>
                      {'#'}
                      {String(complaint.externalId)?.padStart(6, '0')}
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {getStringDate(complaint.createdAt)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {complaint.email}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {complaint.phone}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <CustomChip
                      label={getStatusLabel(complaint.state)}
                      color={getStatusColor(complaint.state)}
                      size="small"
                    />
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => props.onViewDetail(complaint)}
                        style={{color: colors.primary}}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <button
                        type="button"
                        className={`${classes.actionButton} ${
                          complaint.state === 'RECEIVED'
                            ? classes.solveButton
                            : classes.receivedButton
                        }`}
                        onClick={() => handleStatusChange(complaint)}
                        disabled={!hasCustomerComplaintFeature}
                      >
                        {complaint.state === 'RECEIVED'
                          ? 'Marcar Resuelto'
                          : 'Revertir a Recibido'}
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
